<template>
  <div class="v-date-time-picker">
    <v-dialog v-model="modal"
              width="700px"
              :ref="dialogRef"
              :return-value.sync="model"
              :persistent="persistent">
      <template v-slot:activator="{ on }">
        <slot name="field" v-bind="{ on, date, time, timezone }">
          <v-text-field v-on="on"
                        label="Datetime"
                        prepend-icon="event"
                        :value="`${date} ${time} - ${timezones[timezone].label}`"
                        readonly/>
        </slot>
      </template>

      <v-card class="v-date-time-picker-card">
        <v-card-title v-if="$attrs.title" class="headline grey lighten-2" primary-title>
          <v-icon v-if="$attrs.icon" class="mr-1">{{ $attrs.icon }}</v-icon> {{ $attrs.title }}
        </v-card-title>

        <v-card-text>
          <v-autocomplete v-if="!hideTimezone"
                          v-model="timezone"
                          class="mb-4"
                          label="Timezone"
                          item-value="index"
                          item-text="label"
                          :items="timezones"
                          hide-details/>
          <slot name="instructions"/>
          <v-layout row wrap align-content-space-between>
            <v-flex class="pr-2" xs6>
              <v-date-picker v-model="date" :min="$attrs.minDate" scrollable/>
            </v-flex>
            <v-flex class="pl-2" xs6>
              <v-time-picker v-model="time"/>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="secondary" @click="modal = false">Cancel</v-btn>
          <v-btn color="success darken-2" @click="$refs[dialogRef].save({ date, time, timezone })">{{ $attrs.saveText ? $attrs.saveText : 'Save' }}</v-btn>
        </v-card-actions>
      </v-card>

    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { timezones } from '@/services/time'
import ModelBinding from '@/mixins/ModelBinding'

export default {
  name: 'DateTimePicker',
  mixins: [ ModelBinding ],
  props: {
    /**
     * Hide timezone selection
     */
    hideTimezone: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Persist datetime dialog
     */
    persistent: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      modal: false,
      date: null,
      time: null,
      timezone: null,
      timezones
    }
  },
  computed: {
    /**
     * Unique ref property of input field
     */
    dialogRef () {
      return `v-date-time-picker-${this._uid}`
    }
  },
  watch: {
    /**
     * Modal status watcher
     */
    modal: {
      handler (modal) {
        // Restore model value when closing dialog
        if (!modal) {
          if (this.persistent) {
            this.populateModel()
          } else {
            this.model = {
              date: this.date,
              time: this.time,
              timezone: this.timezone
            }
          }
        }
      }
    },
    /**
     * Populate model values to local fields if model changed
     */
    model: {
      handler: 'populateModel',
      immediate: true,
      deep: true
    },
    /**
     * Update date and time values if the timezone changed
     */
    timezone: {
      handler () {
        if (this.date && this.time) {
          const datetime = moment.tz(moment(`${this.date} ${this.time}`), this.timezones[this.timezone].name)
          this.model = {
            timezone: this.timezone,
            date: datetime.format('YYYY-MM-DD'),
            time: datetime.format('HH:mm')
          }
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * Populate model values to local fields
     */
    populateModel () {
      this.$set(this, 'date', this.model.date)
      this.$set(this, 'time', this.model.time)
      this.$set(this, 'timezone', this.model.timezone)
    }
  }
}
</script>

<style lang="sass">
.v-date-time-picker-card
  .v-picker.v-picker--date
    .v-picker__title
      padding: 23px 16px
    .v-picker__body
      padding: 0 0 4px 0
</style>
